import { useState } from 'react';
import dayjs from 'dayjs';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Article } from '@/modules/articles/model/article';
import { AgedDateFormat } from '@/core/date/export';
import { TaggedHeadline } from '@/shared/badges/export';
import articleListStyles from '@/shared/components/flash-highlight.module.scss';
import { opinionsApi } from '@/modules/articles/api';
import ArticleBodyDisplay from '@/modules/articles/components/articles/article-body-display';
import InfiniScroller from '@/modules/articles/components/scrollers/infini-scroller';
import PinButton from '@/modules/articles/components/pins/pin-button';
import BookmarkButton from '@/modules/articles/components/bookmarks/bookmark-button';
import { OpinionMap } from '@/modules/articles/model/opinions/opinion-map';
import { FilterGroup, qCode } from '@/modules/articles/model/concept-item';
import ShowBodyButton from '@/modules/articles/components/scrollers/bullet-list/show-body-button';
import LineClamp from '@/modules/articles/components/scrollers/bullet-list/line-clamp';
import { useGlobalHeadlineView } from '@/core/preferences/export';
import { ViewMode, useWidgetViewMode } from '@/core/widgets/export';

interface BulletListProps {
  section: string[];
  articles?: Article[];

  onArticleClick: Function;
  dark?: boolean;
  title?: JSX.Element;
  filters?: FilterGroup[];
}

export default function BulletList(props: BulletListProps) {
  const [articles, setArticles] = useState<Article[]>();
  const [opinions, setOpinions] = useState<OpinionMap>({});
  const { viewMode, textScale } = useWidgetViewMode();
  const { headlineView: globalHeadlineView } = useGlobalHeadlineView();
  const resolvedMode: ViewMode = viewMode ?? (globalHeadlineView ? 'headline' : 'normal');
  const isHeadline = resolvedMode === 'headline';
  const isReadMore = resolvedMode === 'readMore';

  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});

  const toggleShowBody = (uri: string) => {
    setExpandedMap(prev => ({
      ...prev,
      [uri]: !prev[uri],
    }));
  };

  const loadOpinionsOfBatch = (a: Article[]) => {
    opinionsApi.getOpinionsOfArticles(...a.map(value => value.uri)).then(value => {
      for (const valueKey in value) {
        opinions[valueKey] = value[valueKey];
      }

      setOpinions({ ...opinions });
    });
  };

  const flash = (index: number, date: string) => {
    if (index === 0 && dayjs(date).isAfter(dayjs().subtract(5, 'minute')))
      return articleListStyles.flashArticle;

    return '';
  };

  return (
    <InfiniScroller
      section={props.section}
      filters={props.filters}
      onArticlesChange={setArticles}
      onBatchLoad={loadOpinionsOfBatch}
    >
      {articles?.map((value, index) => {
        const isExpanded = expandedMap[value.uri] ?? false;
        // Genres carry no relevance, so they lead the icons in alphabetical order,
        // followed by the primary subjects ranked by relevance.
        const iconGenres = value.genre
          .filter(g => !!g.icon)
          .sort((a, b) => a.name.localeCompare(b.name));
        const relevantSubjects = value.subject
          .filter(s => !!s.relevance && !!s.icon)
          .sort((a, b) => (a.relevance ?? 0) - (b.relevance ?? 0));
        const iconConcepts = [...iconGenres, ...relevantSubjects];
        return (
          <div
            key={'b-' + value.uri}
            className={`${flash(index, value.firstcreated)}`}
            style={{ fontSize: `${textScale}em` }}
          >
            <div className={'d-flex justify-content-between align-items-baseline mb-1'}>
              <div>
                <AgedDateFormat
                  format={'h:mm a[:]'}
                  fullFormat={'MMM D, h:mm a[:]'}
                  date={value.firstcreated}
                  scale={10}
                />{' '}
                <strong>
                  {iconConcepts.map(s => (
                    <OverlayTrigger
                      key={qCode(s)}
                      placement="top"
                      delay={{ show: 200, hide: 0 }}
                      overlay={<Tooltip id={`subject-tooltip-${qCode(s)}`}>{s.name}</Tooltip>}
                    >
                      <span className="me-1 fs-3">{s.icon}</span>
                    </OverlayTrigger>
                  ))}
                  <TaggedHeadline headline={value.headline} />
                  {isHeadline && (
                    <ShowBodyButton
                      showBody={isExpanded}
                      toggleShowBody={toggleShowBody}
                      uri={value.uri}
                    />
                  )}
                </strong>
              </div>
              <div className={'flex-shrink-0'}>
                <PinButton article={value} />
                <BookmarkButton article={value} providedOpinions={opinions} />
              </div>
            </div>
            <div>
              {(!isHeadline || isExpanded) && (
                <div className={'text-text-dark'}>
                  {isReadMore ? (
                    <LineClamp lines={5}>
                      <ArticleBodyDisplay article={value} xhtml={value.body_xhtml} variant="feed" />
                    </LineClamp>
                  ) : (
                    <ArticleBodyDisplay article={value} xhtml={value.body_xhtml} variant="feed" />
                  )}
                </div>
              )}
            </div>
            <div className={'mb-2 small'}></div>
          </div>
        );
      })}
    </InfiniScroller>
  );
}
